import each from "lodash/each";
import every from "lodash/every";
import filter from "lodash/filter";
import find from "lodash/find";
import findIndex from "lodash/findIndex";
import findLast from "lodash/findLast";
import get from "lodash/get";
import orderBy from "lodash/orderBy";
import sortBy from "lodash/sortBy";
import toNumber from "lodash/toNumber";
import { store } from "../store";

import { differenceInYears, startOfYear } from "date-fns";

import { parseDateString } from "util/dateHelper";

import {
  Attempt,
  Division,
  GetScore,
  LiftName,
  Lifter,
  LifterDivision,
  LiftingOrder,
  Meet,
  ScoreBy,
  WeightClass,
} from "types";
import { getWilksAndAgePoints, getWilksPoints } from "./formulas/wilks";
import { getAgeAdjustedTotal } from "./formulas/age";
import { getIpfAndAgePoints, getIpfPoints } from "./formulas/ipf";
import { getDotsAndAgePoints, getDotsPoints } from "./formulas/dots";
import {
  getSchwartzMaloneAndAgePoints,
  getSchwartzMalonePoints,
} from "./formulas/schwartzMalone";
import { getAhPoints } from "./formulas/ah";
import { getParaDotsPoints } from "./formulas/paraDots";
import {
  getGlossbrennerAndAgePoints,
  getGlossbrennerPoints,
} from "./formulas/glossbrenner";
import { getReshelAndAgePoints, getReshelPoints } from "./formulas/reshel";
import { getKPoints } from "./formulas/kPoints";
import { getPercentOfRecordPoints } from "./formulas/percentOfRecord";
import {
  getWilks2020AndAgePoints,
  getWilks2020Points,
} from "./formulas/wilks2020";
import { getZscoreAndAgePoints, getZscorePoints } from "./formulas/zscore";
import size from "lodash/size";
import { getCompleteLiftingOrder } from "../selectors";
import findLastIndex from "lodash/findLastIndex";

export const getEmptyLifterDivision = (): LifterDivision => {
  return {
    rawOrEquipped: undefined,
    divisionId: undefined,
    declaredAwardsWeightClassId: undefined,
  };
};

export const getDivisionDoc = function (
  divisionId: string | undefined | null,
  meet: Meet,
): Division | undefined {
  if (!divisionId) {
    return undefined;
  }
  return get(meet, ["divisions", divisionId]);
};

export const getLifterDeclaredAwardsWeightClass = function (
  divisionDoc: Division | undefined,
  lifterDivision: LifterDivision | undefined,
) {
  if (!divisionDoc || !lifterDivision?.declaredAwardsWeightClassId) {
    return { name: "" };
  }
  return get(
    divisionDoc,
    ["weightClasses", lifterDivision.declaredAwardsWeightClassId],
    { name: "" },
  );
};

export const getLifterAwardsWeightClassDoc = function (
  meet: Meet,
  lifter: Lifter,
  lifterDivision?: LifterDivision,
) {
  if (!lifterDivision) {
    return { name: "" };
  }
  const divisionDoc = getDivisionDoc(lifterDivision.divisionId, meet);
  const bodyWeight = lifter.bodyWeight;
  if (bodyWeight && divisionDoc) {
    const weightClasses = sortBy(divisionDoc.weightClasses, "maxWeight");

    let lifterWeightClass: WeightClass | undefined;
    every(weightClasses, function (weightClass) {
      if (!lifterWeightClass) {
        if (bodyWeight <= toNumber(weightClass.maxWeight)) {
          lifterWeightClass = weightClass;
          return false; // break loop
        }
      }

      return true; // continue loop
    });
    return lifterWeightClass || { name: "" };
  } else if (get(lifterDivision, "declaredAwardsWeightClassId")) {
    return getLifterDeclaredAwardsWeightClass(divisionDoc, lifterDivision);
  }

  return { name: "" };
};

export const isCompetingInLift = function (
  lifter: Lifter,
  liftName: LiftName,
  meet: Meet,
) {
  return !!find(lifter.divisions, (division) => {
    const divisionDoc = getDivisionDoc(division.divisionId, meet);
    return get(divisionDoc, ["lifts", liftName]);
  });
};

export const isCurrentLifter = function (
  lifter: Lifter,
  meet: Meet,
  platformId: string,
) {
  const currentAttemptId = get(meet, [
    "platforms",
    platformId,
    "currentAttemptId",
  ]);
  let isCurrentLifter = false;
  each(lifter.lifts, (lift) => {
    every(lift, (attempt) => {
      if (attempt && attempt._id === currentAttemptId) {
        isCurrentLifter = true;
        return false; // break loop
      }

      return true; // continue loop
    });
  });

  return isCurrentLifter;
};

export const getLastSuccessfulLiftTimestamp = function (lifter: Lifter) {
  const lifts = get(lifter, "lifts");

  let lastSuccessfulLiftTimestamp = "";

  each(["squat", "bench", "dead"], (liftName) => {
    // Since liftersAttempts is an object ensure order here
    const orderedAttemptArray = [
      get(lifts, [liftName, "1"]),
      get(lifts, [liftName, "2"]),
      get(lifts, [liftName, "3"]),
    ];

    const lastSuccessfulLift = findLast(orderedAttemptArray, (attempt) => {
      return attempt && attempt.weight && attempt.result === "good";
    });

    if (get(lastSuccessfulLift, "changes")) {
      const changeRecord = findLast(lastSuccessfulLift.changes, (change) => {
        return change.attribute === "result" && change.value === "good";
      });

      if (get(changeRecord, "timeStamp")) {
        lastSuccessfulLiftTimestamp = changeRecord.timeStamp;
      }
    }
  });

  return lastSuccessfulLiftTimestamp;
};

export const getMarkedGoodTimeStampForAttempt = (attempt: Attempt) => {
  let lastSuccessfulLiftTimestamp = "";
  if (get(attempt, "changes")) {
    const changeRecord = findLast(attempt.changes, (change) => {
      return change.attribute === "result" && change.value === "good";
    });

    if (get(changeRecord, "timeStamp")) {
      lastSuccessfulLiftTimestamp = changeRecord.timeStamp;
    }
  }
  return lastSuccessfulLiftTimestamp;
};

export const getBestLift = function (
  liftName: LiftName,
  lifter: Lifter,
  forecasted?: boolean,
): Attempt | undefined {
  const liftersAttempts = get(lifter, ["lifts", liftName]);

  // Since liftersAttempts is an object ensure order here
  const orderedAttemptArray = [
    get(liftersAttempts, "1"),
    get(liftersAttempts, "2"),
    get(liftersAttempts, "3"),
  ];

  return findLast(orderedAttemptArray, (attempt) => {
    return (
      attempt &&
      attempt.weight &&
      (attempt.result === "good" || (!attempt.result && forecasted))
    );
  }) as Attempt;
};

export const getTotal: GetScore = ({ lifter, division, meet, forecasted }) => {
  const runningTotal = !!meet.runningTotal;
  let total = 0;
  let isBombOut = false;

  const lifts = division.lifts;

  if (lifts) {
    every(lifts, (isCompetingInLift, liftName) => {
      if (runningTotal) {
        // override logic for bomb out so that we can track total as we go.
        const liftersAttempts = get(lifter, ["lifts", liftName]);

        if (
          (liftersAttempts?.["1"]?.result === "bad" ||
            liftersAttempts?.["1"]?.weight === 0) &&
          (liftersAttempts?.["2"]?.result === "bad" ||
            liftersAttempts?.["2"]?.weight === 0) &&
          (liftersAttempts?.["3"]?.result === "bad" ||
            liftersAttempts?.["3"]?.weight === 0)
        ) {
          isBombOut = true;
          return false; // break loop
        }
      }

      if (isCompetingInLift) {
        const bestLift = getBestLift(liftName as LiftName, lifter, forecasted);
        if (bestLift && bestLift.weight) {
          total += bestLift.weight;
        } else {
          if (!runningTotal) {
            isBombOut = true;
            return false; // break loop
          }
        }
      }

      return true; // continue loop
    });
  }

  if (isBombOut) {
    return 0;
  }

  return total;
};

export const getSubtotal = function (lifter: Lifter, meet: Meet) {
  // only show subtotal if doing all three lifts
  if (
    isCompetingInLift(lifter, "squat", meet) &&
    isCompetingInLift(lifter, "bench", meet) &&
    isCompetingInLift(lifter, "dead", meet)
  ) {
    const bestSquatAttempt = getBestLift("squat", lifter);
    const bestBenchAttempt = getBestLift("bench", lifter);
    if (
      bestSquatAttempt &&
      bestBenchAttempt &&
      bestSquatAttempt.weight &&
      bestBenchAttempt.weight
    ) {
      return bestSquatAttempt.weight + bestBenchAttempt.weight;
    }
  }

  return "";
};

const getFormulaFunction = (scoreBy: ScoreBy | undefined): GetScore => {
  switch (scoreBy) {
    case "BODY_WEIGHT_POINTS":
      return getWilksPoints;
    case "AGE_POINTS":
      return getAgeAdjustedTotal;
    case "BODY_WEIGHT_AND_AGE_POINTS":
      return getWilksAndAgePoints;
    case "IPF_POINTS":
      return getIpfPoints;
    case "IPF_AND_AGE_POINTS":
      return getIpfAndAgePoints;
    case "DOTS_POINTS":
      return getDotsPoints;
    case "DOTS_AND_AGE_POINTS":
      return getDotsAndAgePoints;
    case "SCHWARTZ_MALONE":
      return getSchwartzMalonePoints;
    case "SCHWARTZ_MALONE_AND_AGE_POINTS":
      return getSchwartzMaloneAndAgePoints;
    case "AH_POINTS":
      return getAhPoints;
    case "PARA_DOTS_POINTS":
      return getParaDotsPoints;
    case "GLOSSBRENNER_POINTS":
      return getGlossbrennerPoints;
    case "GLOSSBRENNER_AND_AGE_POINTS":
      return getGlossbrennerAndAgePoints;
    case "RESHEL_POINTS":
      return getReshelPoints;
    case "RESHEL_POINTS_AND_AGE_POINTS":
      return getReshelAndAgePoints;
    case "K_POINTS":
      return getKPoints;
    case "PERCENT_OF_RECORD":
      return getPercentOfRecordPoints;
    case "WILKS_2020_POINTS":
      return getWilks2020Points;
    case "WILKS_2020_AND_AGE_POINTS":
      return getWilks2020AndAgePoints;
    case "ZSCORE_POINTS":
      return getZscorePoints;
    case "ZSCORE_AND_AGE_POINTS":
      return getZscoreAndAgePoints;
    default:
      return getTotal;
  }
};

export const getScore = function ({
  lifter,
  division,
  meet,
  forecasted,
  scoreByOverride,
}: {
  lifter: Lifter;
  division: Division;
  meet: Meet;
  forecasted?: boolean;
  scoreByOverride?: ScoreBy;
}): number {
  const formulaFunction = getFormulaFunction(
    scoreByOverride ?? division.scoreBy,
  );
  if (lifter.gender === "MX") {
    return (
      (getScore({
        lifter: { ...lifter, gender: "MALE" },
        division,
        meet,
        forecasted,
        scoreByOverride,
      }) +
        getScore({
          lifter: { ...lifter, gender: "FEMALE" },
          division,
          meet,
          forecasted,
          scoreByOverride,
        })) /
      2
    );
  }

  return formulaFunction({ lifter, division, meet, forecasted });
};

export const getRealAge = function (lifter: Lifter, meet: Meet) {
  let age = null;
  if (meet.date && lifter.birthDate) {
    const meetDate = parseDateString({ dateString: meet.date, meet });
    const lifterBirthDate = parseDateString({
      dateString: lifter.birthDate,
      meet,
    });
    if (meetDate && lifterBirthDate) {
      age = differenceInYears(meetDate, lifterBirthDate);
    }
  }
  return age;
};

export const getYearAge = function (lifter: Lifter, meet: Meet) {
  let age = null;
  if (meet.date && lifter.birthDate) {
    const meetDate = parseDateString({ dateString: meet.date, meet });
    const lifterBirthDateOriginal = parseDateString({
      dateString: lifter.birthDate,
      meet,
    });
    if (lifterBirthDateOriginal && meetDate) {
      const lifterBirthDate = startOfYear(lifterBirthDateOriginal);
      age = differenceInYears(meetDate, lifterBirthDate);
    }
  }

  return age;
};

export const getLiftersInDivisionAndWeightClass = ({
  meet,
  division,
  weightClass,
}: {
  meet: Meet;
  division: Division;
  weightClass: WeightClass;
}) => {
  return filter(meet.lifters, (lifter) => {
    return !!find(lifter.divisions, (lifterDivision) => {
      if (lifterDivision.divisionId !== division._id) {
        return false;
      }
      const weightClassDoc = getLifterAwardsWeightClassDoc(
        meet,
        lifter,
        lifterDivision,
      );
      return get(weightClassDoc, "_id") === weightClass._id;
    });
  });
};

export const sortLiftersByPlace = ({
  lifters,
  division,
  meet,
  forecasted,
}: {
  lifters: Lifter[];
  division: Division;
  meet: Meet;
  forecasted?: boolean;
}) => {
  let liftingOrder: LiftingOrder | null = null;
  if (forecasted && meet.platforms && size(meet.platforms) === 1) {
    const state = store.getState();
    const platformId = Object.keys(meet.platforms).at(0);
    if (platformId) {
      liftingOrder = getCompleteLiftingOrder(state, {
        match: { params: { meetId: meet._id } },
        platformId: platformId,
      });
    }
  }

  return orderBy(
    lifters,
    [
      (lifter) => getScore({ lifter, division, meet, forecasted }),
      (lifter) => {
        if (division.scoreBy === "TOTAL") {
          return lifter.bodyWeight || 0;
        }

        return 0;
      },
      (lifter) => {
        // Final tie breaker goes to who completes the total/score first.
        // For a single platform meet we can predict who will lift first.
        // For a multi platform meet there is no way to know who will end up lifting first.
        if (forecasted && liftingOrder) {
          return findLastIndex(
            liftingOrder,
            (o) => o.lifter._id === lifter._id,
          );
        }

        return getLastSuccessfulLiftTimestamp(lifter) || "";
      },
    ],
    ["desc", "asc", "asc"],
  );
};

export const getPlace = function (
  lifter: Lifter,
  lifterDivision: LifterDivision,
  meet: Meet,
  forecasted?: boolean,
) {
  const division = getDivisionDoc(lifterDivision.divisionId, meet);
  const lifterWeightClass = getLifterAwardsWeightClassDoc(
    meet,
    lifter,
    lifterDivision,
  );

  if (!division || !lifterWeightClass) {
    return null;
  }

  const lifterTotal = getTotal({ lifter, division, meet, forecasted });
  if (!lifterTotal) {
    return null;
  }

  const divisionLifters = getLiftersInDivisionAndWeightClass({
    meet,
    division,
    weightClass: lifterWeightClass as WeightClass,
  });

  const sortedLifters = sortLiftersByPlace({
    lifters: divisionLifters,
    meet,
    division,
    forecasted,
  });

  const lifterPlace = findIndex(sortedLifters, { _id: lifter._id }) + 1;
  return lifterPlace;
};
